import { useMutation, useQueryClient } from "react-query";
import { isAxiosError } from "axios";
import type { TFunction } from "i18next";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import {
    ConsentGovernanceItem,
    ConsentGovernanceVersion,
    getAdminConsentItemsKey,
    getAdminConsentItemVersionsKey,
    getAdminConsentRecordsKey,
} from "./useGetConsentGovernanceAdmin";
import { buildConsentJsonLocaleHeaders } from "./locale";

export type ConsentGovernanceItemWrite = {
    code: string;
    type: "document" | "opt_in";
    name: string;
    description?: string | null;
    required: boolean;
    active: boolean;
    sortOrder: number;
    category?: string | null;
};

export type ConsentGovernanceItemPatch = {
    active?: boolean;
    sortOrder?: number;
};

export type ConsentGovernanceVersionWrite = {
    consentItem: IRI;
    version: string;
    title: string;
    summary?: string | null;
    content?: string | null;
    documentUrl?: string | null;
    published: boolean;
};

type UpsertConsentItemInput = {
    data: ConsentGovernanceItemWrite;
    id?: number;
    locale?: string;
};

type UpsertConsentItemVersionInput = {
    data: ConsentGovernanceVersionWrite;
    id?: number;
    locale?: string;
};

type PatchConsentItemInput = {
    data: ConsentGovernanceItemPatch;
    id: number;
};

type ConsentGovernanceErrorTranslation =
    | "itemAlreadyExists"
    | "itemHasVersions"
    | "versionArchived"
    | "versionAlreadyExists"
    | "versionHasRecords";

const CONSENT_ERROR_FALLBACKS = {
    itemAlreadyExists: "A consent document or opt-in with this name or technical key already exists.",
    itemHasVersions: "This item cannot be deleted while versions still exist. Delete its versions first.",
    versionArchived: "This archived version is read-only to preserve the audit trail.",
    versionAlreadyExists: "This version already exists for the selected consent item.",
    versionHasRecords: "This version cannot be deleted because audit records already exist.",
} as const satisfies Record<ConsentGovernanceErrorTranslation, string>;

const CONSENT_ERROR_MATCHERS: { tokens: string[]; translation: ConsentGovernanceErrorTranslation }[] = [
    {
        translation: "itemAlreadyExists",
        tokens: [
            "settings.consent.errors.item_already_exists",
            "consent_item_code_unique",
            "consent item with this technical key already exists",
            "consent item with this name or technical key already exists",
        ],
    },
    {
        translation: "versionAlreadyExists",
        tokens: [
            "settings.consent.errors.version_already_exists",
            "consent_item_version_unique",
            "version already exists",
        ],
    },
    {
        translation: "itemHasVersions",
        tokens: [
            "consent-item-has-versions",
            "consent item has versions",
            "cannot be deleted while versions still exist",
        ],
    },
    {
        translation: "versionHasRecords",
        tokens: [
            "consent-version-has-records",
            "consent version has records",
            "cannot be deleted while audit records exist",
        ],
    },
    {
        translation: "versionArchived",
        tokens: ["consent-version-archived", "consent version archived", "archived and cannot be modified"],
    },
];

const API_MESSAGE_FIELDS = ["hydra:description", "detail", "message", "title", "hydra:title"] as const;

const collectStrings = (value: unknown): string[] => {
    if (typeof value === "string") {
        return [value];
    }

    if (Array.isArray(value)) {
        return value.flatMap(collectStrings);
    }

    if (typeof value === "object" && value !== null) {
        return Object.values(value).flatMap(collectStrings);
    }

    return [];
};

const getAxiosPayload = (error: unknown): unknown => {
    if (!isAxiosError(error)) {
        return undefined;
    }

    return error.response?.data;
};

const getPayloadMessages = (payload: unknown): string[] => {
    if (typeof payload === "string") {
        return [payload];
    }

    if (typeof payload !== "object" || payload === null) {
        return [];
    }

    const typedPayload = payload as Record<string, unknown>;
    const violationMessages =
        Array.isArray(typedPayload.violations) ?
            typedPayload.violations.flatMap((violation) => {
                if (typeof violation !== "object" || violation === null) {
                    return [];
                }

                const typedViolation = violation as Record<string, unknown>;
                const message = typedViolation.message ?? typedViolation.title;

                return typeof message === "string" ? [message] : [];
            })
        :   [];

    const payloadMessages = API_MESSAGE_FIELDS.flatMap((field) => {
        const message = typedPayload[field];

        return typeof message === "string" ? [message] : [];
    });

    return [...violationMessages, ...payloadMessages].filter((message) => message.trim().length > 0);
};

export const getConsentGovernanceErrorTranslation = (error: unknown): ConsentGovernanceErrorTranslation | null => {
    const payload = getAxiosPayload(error);
    const searchText = [...collectStrings(payload), error instanceof Error && !isAxiosError(error) ? error.message : ""]
        .join(" ")
        .toLowerCase();

    if (searchText.trim().length === 0) {
        return null;
    }

    return (
        CONSENT_ERROR_MATCHERS.find((matcher) => matcher.tokens.some((token) => searchText.includes(token)))
            ?.translation ?? null
    );
};

export const getConsentGovernanceApiErrorMessage = (error: unknown): string | null => {
    if (isAxiosError(error)) {
        const messages = getPayloadMessages(error.response?.data);

        return messages.length > 0 ? messages.join("\n") : null;
    }

    return error instanceof Error && error.message.trim().length > 0 ? error.message : null;
};

const translateConsentGovernanceError = (translation: ConsentGovernanceErrorTranslation, t: TFunction): string => {
    switch (translation) {
        case "itemAlreadyExists":
            return t("settings.consent.errors.item_already_exists", CONSENT_ERROR_FALLBACKS.itemAlreadyExists);
        case "itemHasVersions":
            return t("settings.consent.delete.item_blocked_versions", CONSENT_ERROR_FALLBACKS.itemHasVersions);
        case "versionArchived":
            return t("settings.consent.version.archived_read_only", CONSENT_ERROR_FALLBACKS.versionArchived);
        case "versionAlreadyExists":
            return t("settings.consent.errors.version_already_exists", CONSENT_ERROR_FALLBACKS.versionAlreadyExists);
        case "versionHasRecords":
            return t("settings.consent.delete.version_blocked_records", CONSENT_ERROR_FALLBACKS.versionHasRecords);
    }
};

export const getConsentGovernanceErrorMessage = (error: unknown, t: TFunction, fallbackMessage: string): string => {
    const translation = getConsentGovernanceErrorTranslation(error);

    if (translation !== null) {
        return translateConsentGovernanceError(translation, t);
    }

    return getConsentGovernanceApiErrorMessage(error) ?? fallbackMessage;
};

const invalidateConsentGovernanceQueries = async (queryClient: ReturnType<typeof useQueryClient>) => {
    await Promise.all([
        queryClient.invalidateQueries([getAdminConsentItemsKey]),
        queryClient.invalidateQueries([getAdminConsentItemVersionsKey]),
        queryClient.invalidateQueries([getAdminConsentRecordsKey]),
    ]);
};

export const useUpsertAdminConsentItem = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (options: UpsertConsentItemInput): Promise<ConsentGovernanceItem> => {
            const { data, id, locale } = options;
            const response =
                id === undefined ?
                    await loggedInApiCall.post("/api/consent-items", data, {
                        headers: buildConsentJsonLocaleHeaders(locale),
                    })
                :   await loggedInApiCall.put(`/api/consent-items/${id}`, data, {
                        headers: buildConsentJsonLocaleHeaders(locale),
                    });

            return response.data;
        },
        onSuccess: async () => {
            await invalidateConsentGovernanceQueries(queryClient);
        },
    });
};

export const usePatchAdminConsentItem = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (options: PatchConsentItemInput): Promise<ConsentGovernanceItem> => {
            const { data, id } = options;
            const response = await loggedInApiCall.patch(`/api/consent-items/${id}`, data, {
                headers: {
                    "Content-Type": "application/json",
                },
            });

            return response.data;
        },
        onSuccess: async () => {
            await invalidateConsentGovernanceQueries(queryClient);
        },
    });
};

export const useUpsertAdminConsentItemVersion = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (options: UpsertConsentItemVersionInput): Promise<ConsentGovernanceVersion> => {
            const { data, id, locale } = options;
            const response =
                id === undefined ?
                    await loggedInApiCall.post("/api/consent-item-versions", data, {
                        headers: buildConsentJsonLocaleHeaders(locale),
                    })
                :   await loggedInApiCall.put(`/api/consent-item-versions/${id}`, data, {
                        headers: buildConsentJsonLocaleHeaders(locale),
                    });

            return response.data;
        },
        onSuccess: async () => {
            await invalidateConsentGovernanceQueries(queryClient);
        },
    });
};

export const useDeleteAdminConsentItem = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: number): Promise<void> => {
            await loggedInApiCall.delete(`/api/consent-items/${id}`);
        },
        onSuccess: async () => {
            await invalidateConsentGovernanceQueries(queryClient);
        },
    });
};

export const useDeleteAdminConsentItemVersion = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: number): Promise<void> => {
            await loggedInApiCall.delete(`/api/consent-item-versions/${id}`);
        },
        onSuccess: async () => {
            await invalidateConsentGovernanceQueries(queryClient);
        },
    });
};
